import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';

const PLAN_NAMES = {
  crm: 'CRM',
  crm_voice: 'CRM + Voice',
  crm_voice_website: 'CRM + Voice + Website',
  crm_voice_website_marketing: 'Full Suite',
  free: 'Free Trial',
};

const PLAN_PRICES = {
  crm: 29,
  crm_voice: 59,
  crm_voice_website: 99,
  crm_voice_website_marketing: 149,
  free: 0,
};

const PLAN_LIMITS = {
  crm: { contacts: 1000, deals: 500 },
  crm_voice: { contacts: 5000, deals: 2000 },
  crm_voice_website: { contacts: 15000, deals: 8000 },
  crm_voice_website_marketing: { contacts: 100000, deals: 50000 },
  free: { contacts: 100, deals: 50 },
};

const PLAN_FEATURES = {
  crm: ['CRM & Pipeline', 'Up to 1,000 contacts', '500 deals', 'Email integration', 'Basic reports'],
  crm_voice: ['Everything in CRM', 'AI Voice calling', 'Up to 5,000 contacts', '2,000 deals', 'Call recordings'],
  crm_voice_website: ['Everything in CRM + Voice', 'Website builder', 'Up to 15,000 contacts', '8,000 deals', 'Custom domain'],
  crm_voice_website_marketing: ['Everything included', 'AI Marketing automation', 'Unlimited contacts', '50,000 deals', 'Priority support'],
  free: ['Up to 100 contacts', '50 deals', 'Basic CRM features'],
};

const SubscriptionTab = () => {
  const { organization } = useAuth();
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [usageData, setUsageData] = useState({ contacts: 0, deals: 0 });
  const [usageLoading, setUsageLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const planType = organization?.plan_type || 'free';
  const subscriptionStatus = organization?.subscription_status || 'trial';
  const stripeCustomerId = organization?.stripe_customer_id;
  const stripeSubscriptionId = organization?.stripe_subscription_id;
  const hasActiveSubscription = subscriptionStatus === 'active' && stripeCustomerId;

  useEffect(() => {
    fetchReceipts();
    fetchUsage();
  }, [organization?.id]);

  const fetchReceipts = async () => {
    if (!organization?.id) return;
    setReceiptsLoading(true);
    try {
      const { data } = await supabase
        ?.from('payment_receipts')
        ?.select('*')
        ?.eq('org_id', organization?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(10);
      setReceipts(data || []);
    } catch (err) { console.error(err); }
    finally { setReceiptsLoading(false); }
  };

  const fetchUsage = async () => {
    if (!organization?.id) return;
    setUsageLoading(true);
    try {
      const [contactsRes, dealsRes] = await Promise.all([
        supabase?.from('contacts')?.select('id', { count: 'exact', head: true })?.eq('org_id', organization?.id),
        supabase?.from('deals')?.select('id', { count: 'exact', head: true })?.eq('org_id', organization?.id),
      ]);
      setUsageData({
        contacts: contactsRes?.count || 0,
        deals: dealsRes?.count || 0,
      });
    } catch (err) { console.error(err); }
    finally { setUsageLoading(false); }
  };

  // Open Stripe Customer Portal (update card, cancel subscription, view invoices)
  const openStripePortal = async (action = 'manage') => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const returnUrl = `${window.location?.origin}/settings?tab=subscription`;
      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: { org_id: organization?.id, return_url: returnUrl, app_url: window.location?.origin },
      });
      if (error || !data?.url) {
        setPortalError(data?.error || error?.message || 'Failed to open billing portal. Please try again.');
        return;
      }
      window.location.href = data?.url;
    } catch (err) {
      setPortalError('Could not open billing portal: ' + (err?.message || 'Unknown error'));
    } finally {
      setPortalLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { label: 'Active', cls: 'bg-green-100 text-green-700' },
      trial: { label: 'Free Trial', cls: 'bg-amber-100 text-amber-700' },
      past_due: { label: 'Past Due', cls: 'bg-red-100 text-red-700' },
      canceled: { label: 'Canceled', cls: 'bg-gray-100 text-gray-600' },
    };
    return map?.[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  };

  const statusBadge = getStatusBadge(subscriptionStatus);
  const planLimits = PLAN_LIMITS?.[planType] || PLAN_LIMITS?.free;
  const planFeatures = PLAN_FEATURES?.[planType] || PLAN_FEATURES?.free;
  const planPrice = PLAN_PRICES?.[planType] || 0;
  const planName = PLAN_NAMES?.[planType] || 'Free Trial';

  const latestReceipt = receipts?.[0];
  const nextBillingDate = latestReceipt
    ? new Date(new Date(latestReceipt?.created_at)?.getTime() + 30 * 24 * 60 * 60 * 1000)
    : null;

  const contactsPercent = planLimits?.contacts > 0
    ? Math.min(100, Math.round((usageData?.contacts / planLimits?.contacts) * 100))
    : 0;
  const dealsPercent = planLimits?.deals > 0
    ? Math.min(100, Math.round((usageData?.deals / planLimits?.deals) * 100))
    : 0;

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="CreditCard" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">Current Plan</h3>
              <p className="text-sm text-muted-foreground">Your active subscription details</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge?.cls}`}>
            {statusBadge?.label}
          </span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="flex items-baseline space-x-2 mb-1">
              <span className="text-3xl font-bold text-foreground">{planName}</span>
              {planPrice > 0 && (
                <span className="text-muted-foreground text-sm">${planPrice}/month</span>
              )}
            </div>
            {nextBillingDate && subscriptionStatus === 'active' && (
              <p className="text-sm text-muted-foreground flex items-center space-x-1.5">
                <Icon name="Calendar" size={14} />
                <span>Next billing: <span className="font-medium text-foreground">{nextBillingDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span></span>
              </p>
            )}
            {subscriptionStatus === 'trial' && (
              <p className="text-sm text-amber-600 flex items-center space-x-1.5 mt-1">
                <Icon name="Clock" size={14} />
                <span>You're on a free trial — upgrade to unlock full features</span>
              </p>
            )}
            {subscriptionStatus === 'past_due' && (
              <p className="text-sm text-red-600 flex items-center space-x-1.5 mt-1">
                <Icon name="AlertTriangle" size={14} />
                <span>Payment overdue — please update your payment method</span>
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/billing')}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="Zap" size={15} />
              <span>{subscriptionStatus === 'active' ? 'Change Plan' : 'Upgrade Now'}</span>
            </button>
            {subscriptionStatus === 'active' && (
              <button
                onClick={() => navigate('/billing')}
                className="flex items-center justify-center space-x-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
              >
                <Icon name="Receipt" size={15} />
                <span>View Invoices</span>
              </button>
            )}
          </div>
        </div>

        {/* Plan Features */}
        <div className="mt-5 pt-5 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Included in your plan</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {planFeatures?.map((feature, i) => (
              <div key={i} className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-green-600 flex-shrink-0" />
                <span className="text-sm text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Usage Metrics */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <Icon name="BarChart2" size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Usage</h3>
            <p className="text-sm text-muted-foreground">Your current resource usage this billing period</p>
          </div>
        </div>

        {usageLoading ? (
          <div className="text-sm text-muted-foreground">Loading usage data...</div>
        ) : (
          <div className="space-y-5">
            {/* Contacts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={15} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Contacts</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{usageData?.contacts?.toLocaleString()}</span>
                  {' / '}
                  {planLimits?.contacts >= 100000 ? 'Unlimited' : planLimits?.contacts?.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    contactsPercent >= 90 ? 'bg-red-500' :
                    contactsPercent >= 70 ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${contactsPercent}%` }}
                />
              </div>
              {contactsPercent >= 80 && (
                <p className="text-xs text-amber-600 mt-1 flex items-center space-x-1">
                  <Icon name="AlertTriangle" size={11} />
                  <span>Approaching limit — consider upgrading</span>
                </p>
              )}
            </div>

            {/* Deals */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Briefcase" size={15} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Deals</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{usageData?.deals?.toLocaleString()}</span>
                  {' / '}
                  {planLimits?.deals >= 50000 ? 'Unlimited' : planLimits?.deals?.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    dealsPercent >= 90 ? 'bg-red-500' :
                    dealsPercent >= 70 ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${dealsPercent}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Payment Method — with real card update/delete via Stripe Portal */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
              <Icon name="Wallet" size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">Payment Method</h3>
              <p className="text-sm text-muted-foreground">Update or remove your card on file</p>
            </div>
          </div>
        </div>

        {/* Portal error */}
        {portalError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <Icon name="AlertCircle" size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-600">{portalError}</p>
          </div>
        )}

        {hasActiveSubscription ? (
          <div className="space-y-4">
            {/* Card on file display */}
            <div className="flex items-center space-x-3 p-4 bg-muted/40 rounded-xl border border-border">
              <div className="w-10 h-7 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center flex-shrink-0">
                <Icon name="CreditCard" size={14} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Card on file</p>
                <p className="text-xs text-muted-foreground truncate">
                  Stripe Customer: <code className="font-mono">{stripeCustomerId?.slice(0, 18)}...</code>
                </p>
              </div>
              <span className="text-xs text-green-600 font-medium flex items-center space-x-1 flex-shrink-0">
                <Icon name="CheckCircle" size={12} />
                <span>Active</span>
              </span>
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Update Card */}
              <button
                onClick={() => openStripePortal('update')}
                disabled={portalLoading}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {portalLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Icon name="CreditCard" size={15} />
                )}
                <span>Update Card</span>
              </button>

              {/* Manage Subscription (cancel, view invoices, etc.) */}
              <button
                onClick={() => openStripePortal('manage')}
                disabled={portalLoading}
                className="flex items-center justify-center space-x-2 px-4 py-3 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-60"
              >
                {portalLoading ? (
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <Icon name="Settings" size={15} />
                )}
                <span>Manage Subscription</span>
              </button>
            </div>

            {/* Cancel subscription — shows confirm step */}
            {!showCancelConfirm ? (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 border border-red-200 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <Icon name="XCircle" size={15} />
                <span>Cancel Subscription</span>
              </button>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm font-semibold text-red-700 mb-1">Cancel your subscription?</p>
                <p className="text-xs text-red-600 mb-4">
                  You'll be redirected to Stripe to confirm cancellation. Your plan remains active until the end of the billing period.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="flex-1 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                  >
                    Keep Plan
                  </button>
                  <button
                    onClick={() => openStripePortal('cancel')}
                    disabled={portalLoading}
                    className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center space-x-2"
                  >
                    {portalLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : null}
                    <span>Yes, Cancel</span>
                  </button>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground text-center">
              <Icon name="Lock" size={11} className="inline mr-1" />
              Card management is handled securely by Stripe
            </p>
          </div>
        ) : stripeCustomerId ? (
          // Has customer ID but not active (past_due, canceled)
          (<div className="space-y-3">
            <div className="flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <Icon name="AlertTriangle" size={18} className="text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-700">Subscription {subscriptionStatus}</p>
                <p className="text-xs text-amber-600">Open the billing portal to update your payment method or reactivate</p>
              </div>
            </div>
            <button
              onClick={() => openStripePortal('manage')}
              disabled={portalLoading}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {portalLoading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Icon name="ExternalLink" size={15} />
              )}
              <span>Open Billing Portal</span>
            </button>
          </div>)
        ) : (
          // No payment method at all
          (<div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mb-3">
              <Icon name="CreditCard" size={22} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">No payment method on file</p>
            <p className="text-xs text-muted-foreground mb-4">Add a payment method to subscribe to a plan</p>
            <button
              onClick={() => navigate('/billing')}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90"
            >
              <Icon name="Plus" size={14} />
              <span>Add Payment Method</span>
            </button>
          </div>)
        )}
      </div>
      {/* Recent Invoices */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Receipt" size={18} className="text-muted-foreground" />
            <h3 className="text-base font-semibold text-card-foreground">Recent Invoices</h3>
          </div>
          {receipts?.length > 0 && (
            <button
              onClick={() => navigate('/billing')}
              className="text-xs text-primary hover:underline font-medium"
            >
              View all
            </button>
          )}
        </div>

        {receiptsLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading invoices...</div>
        ) : receipts?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileText" size={28} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No invoices yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {receipts?.map((receipt) => (
              <div key={receipt?.id} className="px-6 py-4 flex items-center justify-between hover:bg-muted/20">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={15} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {PLAN_NAMES?.[receipt?.plan_id] || receipt?.plan_id || 'Subscription'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(receipt?.created_at)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">
                    ${((receipt?.amount_cents || 0) / 100)?.toFixed(2)}
                  </p>
                  {receipt?.promo_code && (
                    <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                      {receipt?.promo_code}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionTab;
